<template>
  <v-row id="networth-charts">
    <v-col cols="12" md="12" lg="6" class="py-0 my-0 mt-3 mb-2">
      <v-card
        v-if="totalDonutChartData.datasets"
        class="networth-charts-card elevation-0 pa-2 pb-0 mt-0"
      >
        <v-card-title>
          <span class="card-title">
            {{ $t('views.netWorth.charts.groups') }}
          </span>
        </v-card-title>
        <v-card-text class="mt-3 pb-1">
          <DonutChart
            ref="totalDonutChart"
            :chartData="totalDonutChartData"
            :chartOptions="donutChartOptions"
          />
        </v-card-text>
      </v-card>
    </v-col>

    <template v-if="groupSelected">
      <v-col cols="12" md="12" lg="6" class="py-0 my-0 mt-3 mb-2">
        <v-card class="networth-charts-card elevation-0 pa-2 pb-0 mt-0">
          <v-card-title>
            <span class="card-title">
              {{ $t('views.netWorth.charts.accounts') }}
            </span>
            <v-spacer />
            <div v-if="groupSelected && groups" class="type-selector">
              <v-select
                v-model="groupSelected"
                :items="groups"
                :label="$t('views.netWorth.group')"
                dense
                hide-details
              />
            </div>
          </v-card-title>
          <v-card-text class="mt-3 pb-1">
            <DonutChart
              ref="groupDonutChart"
              :chartData="groupDonutChartData"
              :chartOptions="donutChartOptions"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </template>
  </v-row>
</template>

<script>
import { map } from 'lodash'
import { mapState } from 'vuex'
import { DONUT_PALETTE, DARK_DEFAULT_PALETTE } from '@/lib/colors.js'
import { getCurrencyValueAsString } from '@/lib/utils/currencies.js'
import DonutChart from '@/views/components/core/chartjs/DonutChart'

export default {
  components: { DonutChart },
  props: {
    netWorthResult: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    groups: [],
    groupSelected: null,
    totalDonutChartData: {
      labels: null,
      datasets: null,
    },
    groupDonutChartData: {
      labels: null,
      datasets: null,
    },
  }),
  computed: {
    ...mapState(['user', 'hideAmounts']),
    donutChartOptions() {
      const context = this

      return {
        maintainAspectRatio: false,
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) label += ': '

              const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]

              label += getCurrencyValueAsString({
                value,
                currency: context.user.baseCurrency,
                hide: context.hideAmounts,
              })

              let percent =
                data.datasets[tooltipItem.datasetIndex].additionalData.percents[tooltipItem.index]
              percent = getCurrencyValueAsString({
                value: percent,
              })
              return `${label} | ${percent}%`
            },
            title: function (tooltipItems, data) {
              return data.labels[tooltipItems[0].index] || ''
            },
          },
        },
      }
    },
  },
  watch: {
    '$vuetify.theme.dark'() {
      this.setTotalChartData()
      this.setGroupChartData()
    },
    netWorthResult: {
      handler() {
        if (!this.netWorthResult.groups) {
          this.groupSelected = null
          return
        }
        this.groups = this.netWorthResult.groups.map((group) => {
          return {
            text: group.name,
            value: group,
          }
        })
        this.groupSelected = this.groups[0].value

        this.setTotalChartData()
        this.setGroupChartData()
      },
      deep: true,
      immediate: true,
    },
    groupSelected() {
      this.setGroupChartData()
    },
  },
  methods: {
    setGroupChartData() {
      const accounts = this.groupSelected.accounts
      this.groupDonutChartData.datasets = [
        {
          label: this.$t(`common.total`),
          data: map(accounts, 'value'),
          backgroundColor: Object.values(DONUT_PALETTE),
          borderColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.dark_backgroundLighterBlack
            : 'white',
          additionalData: {
            percents: map(accounts, (item) => {
              return item.weight
            }),
          },
        },
      ]
      this.groupDonutChartData.labels = map(accounts, (elem) => {
        return elem.name
      })
      if (this.$refs.groupDonutChart) this.$refs.groupDonutChart.render()
    },
    setTotalChartData() {
      const groups = this.netWorthResult.groups.filter((elem) => {
        return elem.total >= 0
      })
      this.totalDonutChartData.datasets = [
        {
          label: this.$t(`common.total`),
          data: map(groups, 'total'),
          backgroundColor: Object.values(DONUT_PALETTE),
          borderColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.dark_backgroundLighterBlack
            : 'white',
          additionalData: {
            percents: map(groups, (item) => {
              return item.weight
            }),
          },
        },
      ]
      this.totalDonutChartData.labels = map(groups, (elem) => {
        return elem.name
      })
      if (this.$refs.totalDonutChart) this.$refs.totalDonutChart.render()
    },
  },
}
</script>

<style lang="scss">
#networth-charts {
  .networth-charts-card {
    height: 100%;
    .type-selector {
      width: 120px;
    }
  }
}
</style>
